import EventEmitter from "events";

export type EmitterEventsMap = Record<string | symbol, unknown>;

export type TypedEventEmitter<T extends EmitterEventsMap> = Omit<
  EventEmitter,
  "emit" | "on" | "once" | "off"
> & {
  emit: <EventName extends keyof T>(
    eventName: EventName,
    ...args: T[EventName] extends void ? [] : [T[EventName]]
  ) => void;
  on: <EventName extends keyof T>(
    eventName: EventName,
    handler: (arg: T[EventName]) => unknown,
  ) => void;
  once: <EventName extends keyof T>(
    eventName: EventName,
    handler: (arg: T[EventName]) => unknown,
  ) => void;
  off: <EventName extends keyof T>(
    eventName: EventName,
    handler: (arg: T[EventName]) => unknown,
  ) => void;
};

export type TypedEventEmitterEvents<
  T extends TypedEventEmitter<EmitterEventsMap>,
> = T extends TypedEventEmitter<infer EM> ? EM : never;

export function createTypedEventEmitter<EventMap extends EmitterEventsMap>() {
  return new EventEmitter() as TypedEventEmitter<EventMap>;
}

// Helper to create a promise that waits for an event on a TypedEventEmitter.
// Ported from Open Sourcerer (src/api/util.ts) with the Scheduler.
export function typedEmitterPromise<
  EventMapType extends EmitterEventsMap,
  EventType extends keyof EventMapType,
  RejectEventType extends keyof EventMapType,
>(
  emitter: TypedEventEmitter<EventMapType>,
  event: EventType,
  rejectEvent?: RejectEventType,
): Promise<EventMapType[EventType]> {
  let resolved = false;
  return new Promise((resolve, reject) => {
    emitter.setMaxListeners(emitter.getMaxListeners() + (rejectEvent ? 2 : 1));
    emitter.once(event, (arg) => {
      emitter.setMaxListeners(emitter.getMaxListeners() - 1);
      if (resolved) return;
      resolved = true;
      resolve(arg);
    });
    if (rejectEvent) {
      emitter.once(rejectEvent, (arg) => {
        emitter.setMaxListeners(emitter.getMaxListeners() - 1);
        if (resolved) return;
        resolved = true;
        reject(arg);
      });
    }
  });
}
